import { useQuery } from "@tanstack/react-query";
import { logout } from "../hooks/useSession.js";
import { fetchEarningsMe, fetchDbMe } from "../api.js";
import { setView, useView, VIEW_LABELS, type View } from "../store/view.js";

// Shared top navigation. Rendered at the top of every page — kanban Board,
// Cleaner Earnings, and the stub pages for Business Growth / Marketing /
// Tech Coverage. Picks up the current view from useView() and triggers
// page swaps via setView().
//
// Visibility rule:
//   - Pipeline is always visible.
//   - Cleaner Earnings only shows up if the server says the logged-in user
//     is in EARNINGS_ADMIN_EMAILS (or the global fallback). This avoids
//     teasing staff with an admin-gated tab they can't open.
//   - Stubs (Growth / Marketing / Coverage) always show, marked "Soon".

type Props = {
  userName: string;
  // Right-hand status indicators rendered before the user name. Optional —
  // the kanban Board pipes its loading/error state in here so the existing
  // visible-affordances stay where users expect them.
  status?: React.ReactNode;
};

const PIPELINE: View = "pipeline";
const SCHEDULE: View = "schedule";
const EARNINGS: View = "earnings";
const GROWTH: View = "growth";
const MARKETING: View = "marketing";
const COVERAGE: View = "coverage";
const AREAS: View = "areas";
const RECRUITMENT: View = "recruitment";
const WAITINGLIST: View = "waitinglist";
const TEAM: View = "team";
const DB: View = "db";
// All admin dashboards are live. Stubs list left empty for the day a new
// view ID is added that needs the "SOON" treatment.
const STUBS: View[] = [];

export function TopNav({ userName, status }: Props): JSX.Element {
  const view = useView();
  const me = useQuery({
    queryKey: ["earnings", "me"],
    queryFn: fetchEarningsMe,
    staleTime: 5 * 60_000,
  });
  const isEarningsAdmin = me.data?.isEarningsAdmin ?? false;

  // DB board is hard-gated to one specific email at the API. The /api/db/me
  // endpoint returns 403 for everyone else, so a failed query here means
  // "not the owner — hide the tab". We only fire the query for users who
  // already passed the earnings-admin gate to avoid noise in server logs.
  const dbMe = useQuery({
    queryKey: ["db", "me"],
    queryFn: fetchDbMe,
    staleTime: 5 * 60_000,
    retry: false,
    enabled: isEarningsAdmin,
  });
  const isDbOwner = dbMe.data?.isOwner ?? false;

  return (
    <header className="flex items-center justify-between border-b border-ink-700 bg-ink-900/80 px-6 py-3 backdrop-blur-sm print:hidden">
      <div className="flex items-baseline gap-5">
        <span className="text-2xl leading-none text-ink-50">Breeze</span>
        <nav className="flex items-baseline gap-1">
          <NavLink view={PIPELINE} active={view === PIPELINE} />
          {isEarningsAdmin && (
            <>
              <NavLink view={SCHEDULE} active={view === SCHEDULE} />
              <NavLink view={EARNINGS} active={view === EARNINGS} />
              <NavLink view={GROWTH} active={view === GROWTH} />
              <NavLink view={MARKETING} active={view === MARKETING} />
              <NavLink view={COVERAGE} active={view === COVERAGE} />
              <NavLink view={AREAS} active={view === AREAS} />
              <NavLink view={RECRUITMENT} active={view === RECRUITMENT} />
              <NavLink view={WAITINGLIST} active={view === WAITINGLIST} />
              <NavLink view={TEAM} active={view === TEAM} />
              {isDbOwner && <NavLink view={DB} active={view === DB} />}
            </>
          )}
          {STUBS.map((v) => (
            <NavLink key={v} view={v} active={view === v} stub />
          ))}
        </nav>
      </div>
      <div className="flex items-center gap-3 text-[13px] text-ink-50">
        {status}
        <span>{userName}</span>
        <button
          onClick={() => void logout()}
          className="rounded-md border border-ink-500 px-2 py-1 text-ink-50 hover:border-ink-400 hover:bg-ink-700"
          title="Sign out"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

function NavLink({
  view,
  active,
  stub,
}: {
  view: View;
  active: boolean;
  stub?: boolean;
}): JSX.Element {
  return (
    <button
      onClick={() => setView(view)}
      className={`rounded-md px-3 py-1.5 text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors ${
        active
          ? "bg-slack-aubergine text-ink-50"
          : "text-ink-100 hover:bg-ink-700 hover:text-ink-50"
      }`}
    >
      <span>{VIEW_LABELS[view]}</span>
      {stub && (
        <span className="ml-2 rounded-sm bg-ink-700 px-1.5 py-0.5 text-[10px] tracking-wider text-ink-100">
          SOON
        </span>
      )}
    </button>
  );
}
